import { clsx } from "clsx";
import { Link, NavLink } from "react-router-dom";

import WorkspaceSelector from "common/components/WorkspaceSelector";
import { getWhitelabelCustomization } from "common/helpers/utils";
import ExpandCollapseButton from "common/layouts/WorkspaceLayout/DesktopLayout/DesktopNav/ExpandCollapseButton";
import ExtraNavItems from "common/layouts/WorkspaceLayout/ExtraNavItems";
import type { NavItemType } from "common/types";

import NavItem from "../../NavItem";
import TooltipNavItem from "../../TooltipNavItem";
import ProfileDropdown from "../ProfileDropdown";
import DebugPopup from "./DebugPopup";

interface DesktopNavProps {
  navItems: NavItemType[];
  isExpanded: boolean;
  setIsExpanded: (isExpanded: boolean) => void;
}

export default function DesktopNav({
  navItems,
  isExpanded,
  setIsExpanded,
}: DesktopNavProps) {
  const { whitelabelCustomization } = getWhitelabelCustomization();
  const expandCollapseButtonClassName = "transition-[opacity]";

  return (
    <nav
      className={clsx(
        "fixed left-2 z-nav flex flex-col transition-[width] duration-300 h-[calc(100%-16px)] top-2",
        isExpanded ? "w-52" : "w-[44px]",
      )}
    >
      {whitelabelCustomization ? (
        <div className="flex items-center px-1 py-2.5 mt-2.5 mb-3 overflow-hidden">
          <div className="flex-1 flex relative group">
            <Link to="home" className="flex items-center h-9">
              <img
                key={isExpanded.toString()}
                src={
                  isExpanded
                    ? whitelabelCustomization.logo
                    : whitelabelCustomization.favicon
                }
                alt=""
                className={clsx(
                  "transition-[padding,opacity] max-h-full",
                  isExpanded ? "px-4" : "group-hover:opacity-0 mr-auto",
                )}
              />
            </Link>
            <div
              className={clsx(
                "flex items-center justify-center absolute inset-0",
                isExpanded && "pointer-events-none",
              )}
            >
              <ExpandCollapseButton
                className={clsx(
                  expandCollapseButtonClassName,
                  "opacity-0 size-full items-center justify-center transition-opacity",
                  !isExpanded && "group-hover:opacity-100",
                )}
                isExpanded={isExpanded}
                onClick={() => setIsExpanded(!isExpanded)}
              />
            </div>
          </div>
          <ExpandCollapseButton
            className={clsx(
              expandCollapseButtonClassName,
              !isExpanded && "hidden",
            )}
            isExpanded={isExpanded}
            onClick={() => setIsExpanded(!isExpanded)}
          />
        </div>
      ) : (
        <div className="flex flex-col px-1 mt-3 gap-y-1.5 items-start">
          <div className="flex gap-x-1.5 flex-1 items-center w-full">
            <WorkspaceSelector isExpanded={isExpanded} />
            <ExpandCollapseButton
              className={clsx(
                expandCollapseButtonClassName,
                isExpanded ? "delay-300" : "opacity-0 pointer-events-none",
              )}
              isExpanded={isExpanded}
              onClick={() => setIsExpanded(!isExpanded)}
            />
          </div>
          <ExpandCollapseButton
            className={clsx(
              expandCollapseButtonClassName,
              "ml-2.5",
              isExpanded ? "opacity-0 pointer-events-none" : "delay-300",
            )}
            onClick={() => setIsExpanded(!isExpanded)}
            isExpanded={isExpanded}
          />
        </div>
      )}
      <div className="flex flex-col gap-y-1 px-1 py-1.5">
        {navItems.map(({ path, icon, label }) => {
          const navComponent = (
            <NavLink
              data-intercom-target={`nav-link-${path}`}
              data-cy={`nav-link-${path}`}
              key={path}
              to={path}
            >
              {({ isActive }) => (
                <NavItem
                  isExpanded={isExpanded}
                  icon={icon}
                  isActive={isActive}
                  isOnboarding={path === "onboarding"}
                >
                  {label}
                </NavItem>
              )}
            </NavLink>
          );

          return (
            <TooltipNavItem
              key={path}
              label={label}
              component={navComponent}
              isExpanded={isExpanded}
            />
          );
        })}
      </div>
      <div className="mt-auto flex gap-y-1 flex-col px-1">
        {import.meta.env.MODE !== "production" && (
          <DebugPopup isExpanded={isExpanded} />
        )}
        <ExtraNavItems isExpanded={isExpanded} />
      </div>
      <ProfileDropdown isExpanded={isExpanded} />
    </nav>
  );
}
